'use client';

import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  try {
    const response = await fleetApi.post(url + getTzParam(), payload);
    return response.data;
  } catch (err) {
    console.error('API fetcher error:', { url, payload, status: err?.response?.status, data: err?.response?.data });
    throw err;
  }
};

export const revalidateVehicleLicenseList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/vehicle-license/search');
};

export const useVehicleLicenseList = (page, rowsPerPage, searchKey, searchValue, companyId = '') => {
  const start = page * rowsPerPage;
  const payload = useMemo(() => {
    const fb = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .like(searchKey, searchValue, 'AND')
      .addJoin('company', ['name'])
      .setOrder(['license_name'], true);
    if (companyId) {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    } else {
      fb.filterByCompany();
    }

    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companyId]);
  const key = useMemo(() => ['/vehicle-license/search', JSON.stringify(payload)], [payload]);
  const {
    data,
    error,
    isLoading: swrLoading
  } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  const rows = Array.isArray(data) ? data : data?.data || [];
  const total = data?.totalRecord ?? data?.totalRecords ?? data?.dataSize ?? rows.length;

  return {
    data: rows,
    totalRecord: total,
    isLoading: swrLoading,
    isError: error
  };
};

export const getVehicleLicenseById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const resp = await fleetApi.get(`/vehicle-license/${id}${getTzParam()}`);
    if (resp?.data) return resp.data;
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).filterByCompany().equal('id', String(id));

    const payload = fb.build();
    const response = await fleetApi.post(`/vehicle-license/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);
    return Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
  } catch (err) {
    console.error('Failed to fetch vehicle license by id:', err);
    throw err;
  }
};

export const createVehicleLicense = async (formData = {}) => {
  try {
    const body = { ...formData };
    const response = await fleetApi.post(`/vehicle-license/new${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateVehicleLicenseList();
    return response.data;
  } catch (err) {
    console.error('Error creating vehicle license:', err);
    throw err;
  }
};

export const updateVehicleLicense = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = { ...formData };
    const response = await fleetApi.put(`/vehicle-license/edit/${id}${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateVehicleLicenseList();
    mutate(`/vehicle-license/${id}`);
    return response.data;
  } catch (err) {
    console.error('Error updating vehicle license:', err);
    throw err;
  }
};

export const deleteVehicleLicense = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/vehicle-license/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateVehicleLicenseList();
    return response.data;
  } catch (err) {
    console.error('Error deleting vehicle license:', err);
    throw err;
  }
};
